import type { Icon } from '@phosphor-icons/react'
import { createContext, useContext, type ComponentType, type ReactNode } from 'react'
import type { NavDef } from './nav.tsx'

/**
 * What differs between the two sites built from this code: the contributor app (horizon.robotensor.ai, also the
 * phone's account pages) and ops (ops.robotensor.ai, staff only). The shell, sign-in and palette read it.
 */
export interface SiteConfig {
  id: 'app' | 'ops'
  /** Shown next to the shard mark and in page titles. */
  name: string
  nav: readonly NavDef[]
  /** Sidebar groups in order; groups without visible items are hidden. */
  groups: readonly { id: string; label: string }[]
  /** Entries in the person's menu, between their email and the theme. */
  accountLinks: readonly { key: string; label: string; icon: ReactNode; keys?: string; to: string }[]
  /** Pages on other sites (the guide, the public dashboard): in the person's menu and the palette's Help group. */
  helpLinks?: readonly { key: string; label: string; icon: ReactNode; href: string; keywords?: string[] }[]
  /** Where a pasted id goes on this site; null for kinds with no page here. */
  routeForId: (id: string, perms: ReadonlySet<string>, userId: string) => IdRoute | null
  signIn: { title: string; lede: string; plate: readonly { label: string; text: string; icon?: Icon }[]; discord: boolean; google: boolean }
  /** Ask for name, country and consents (/welcome) before the site opens. */
  onboarding: boolean
  /** Extra controls at the start of the top bar (ops: the live event indicator). */
  topbar?: ComponentType<{ perms: ReadonlySet<string> }>
}

export interface IdRoute {
  to: string
  search?: Record<string, string>
  label: string
}

const SiteContext = createContext<SiteConfig | null>(null)

export function SiteProvider({ site, children }: { site: SiteConfig; children: ReactNode }) {
  return <SiteContext.Provider value={site}>{children}</SiteContext.Provider>
}

export function useSite(): SiteConfig {
  const s = useContext(SiteContext)
  if (!s) throw new Error('useSite outside SiteProvider')
  return s
}
